import { LabelGroup } from "@dropbox/dash-component-library";
import { List } from "@dropbox/dig-components/list";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  BriefcaseLine,
  CelebrateLine,
  ClockLine,
  LocationLine,
  OutOfOfficeLine,
  SendLine,
} from "@dropbox/dig-icons/assets";
import { Link } from "@mirage/link/Link";
import {
  calculateTenure,
  calculateTenurePercentile,
  formatBirthday,
  formatHireDate,
  formatLocation,
  formatUpcomingPTO,
  getLocalTime,
  isMoreThanToday,
  toMetro,
} from "@mirage/mosaics/UserProfilePage/utils";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import i18n from "@mirage/translations";
import { Fragment, FunctionComponent } from "react";

import type { UserInfo } from "@mirage/mosaics/UserProfilePage/types";
import styles from "./UserProfileDetails.module.css";

export const UserProfileDetails = ({ user }: { user: UserInfo }) => {
  const currentAccount = useDropboxAccount();

  const calculateTimeDifference = (timezoneOffset: number) => {
    try {
      const userOffsetHours = timezoneOffset / 3600;
      const localOffsetHours = -new Date().getTimezoneOffset() / 60;
      const diff = userOffsetHours - localOffsetHours;

      const roundedDiff = Math.round(diff); // Round to nearest hour
      const absDiff = Math.abs(roundedDiff);

      if (absDiff === 0) {
        return i18n.t("same_timezone_as_yours");
      } else {
        return i18n.t("current_time_info", {
          countHours: absDiff,
          hours: absDiff === 1 ? i18n.t("hour") : i18n.t("hours"),
          aheadOrBehind:
            roundedDiff > 0
              ? i18n.t("timezone_ahead")
              : i18n.t("timezone_behind"),
        });
      }
    } catch (e) {
      return "";
    }
  };

  const isBoomerang = (tenure: number, hireDate: string): boolean => {
    const current = new Date();
    let [month, year] = hireDate.split("/").map(Number);
    if (!hireDate.includes("/")) {
      [year, month] = hireDate.split("-").map(Number);
    }

    const hired = new Date(year, month - 1, 1);

    let years = current.getFullYear() - hired.getFullYear();
    let months = current.getMonth() - hired.getMonth();
    if (months < 0) {
      years--;
      months += 12;
    }

    const monthsSinceHireDate = years * 12 + months;

    return Math.abs(tenure - monthsSinceHireDate) > 1;
  };

  const isValidEmail = (email: string): boolean => {
    // contractor email is not assigned and hide it from UI.
    return email !== "donotuse@dropbox.com";
  };

  const details = [
    <Item
      key="contact"
      icon={SendLine}
      primary={i18n.t("profile_contact")}
      secondary={
        <>
          {user.email && isValidEmail(user.email) && (
            <Link
              variant="monochromatic"
              hasNoUnderline
              href="mailto:${user.email}"
              key={user.email}
            >
              {"@" + user.user_id}
            </Link>
          )}
        </>
      }
      supporting={
        user.email && isValidEmail(user.email) ? user.email : undefined
      }
      supportingSize="small"
    />,
  ];

  if (user.location) {
    details.push(
      <Item
        icon={LocationLine}
        primary={i18n.t("location")}
        secondary={formatLocation(user.city) || toMetro(user.location)}
      />,
    );
  }

  const timezoneOffset = user.timezone_offset;
  if (timezoneOffset) {
    details.push(
      <Item
        icon={ClockLine}
        primary={i18n.t("profile_current_time")}
        secondary={getLocalTime(timezoneOffset)}
        supporting={
          user.email !== currentAccount?.email
            ? calculateTimeDifference(timezoneOffset)
            : ""
        }
      />,
    );
  }

  if (user?.hire_date && user?.tenure != null) {
    details.push(
      <Item
        icon={BriefcaseLine}
        primary={i18n.t("tenure")}
        secondary={calculateTenure(user.tenure)}
        supporting={
          (isBoomerang(user.tenure, user.hire_date)
            ? i18n.t("first_started")
            : i18n.t("started")) +
          " " +
          formatHireDate(user.hire_date)
        }
        withTooltip={
          user.tenure_percentile
            ? i18n.t("tenure_percentile", {
                percentile: calculateTenurePercentile(user.tenure_percentile),
              })
            : undefined
        }
      />,
    );
  }

  const upcomingPTO = user.upcoming_pto_list?.[0];
  if (
    upcomingPTO?.start_date &&
    upcomingPTO?.end_date &&
    isMoreThanToday(upcomingPTO.start_date)
  ) {
    details.push(
      <Item
        icon={OutOfOfficeLine}
        primary={i18n.t("profile_pto")}
        secondary={formatUpcomingPTO(upcomingPTO)}
      />,
    );
  }

  if (user.birthday) {
    details.push(
      <Item
        icon={CelebrateLine}
        primary={i18n.t("birthday")}
        secondary={formatBirthday(user.birthday)}
      />,
    );
  }

  return (
    <div className={styles.container}>
      {details.map((detail, index) => (
        <Fragment key={index}>{detail}</Fragment>
      ))}
    </div>
  );
};

interface ItemProps {
  icon: React.ComponentType<React.SVGAttributes<SVGElement>>;
  primary: string;
  secondary?: string | React.ReactNode;
  supporting?: string | React.ReactNode;
  supportingSize?: "small" | "xsmall";
  withTooltip?: string;
}

const Item: FunctionComponent<ItemProps> = ({
  icon,
  primary,
  secondary,
  supporting,
  withTooltip,
  supportingSize = "xsmall",
}) => {
  return (
    <List.Item
      style={{
        display: "flex",
        alignItems: "flex-start",
        alignContent: "flex-start",
        width: "240px",
        padding: "12px 4px",
      }}
    >
      <List.Accessory>
        <UIIcon src={icon} isOpticallyAligned />
      </List.Accessory>
      <List.Content>
        <LabelGroup
          verticalAlignment="top"
          withLabel={
            <>
              <Text variant="label">{primary}</Text>
            </>
          }
          withSubText={
            <>
              {secondary && withTooltip && (
                <DigTooltip title={withTooltip} placement="bottom">
                  <Text variant="label" size="small" color="subtle">
                    {secondary}
                  </Text>
                </DigTooltip>
              )}
              {secondary && !withTooltip && (
                <Text variant="label" size="small" color="subtle">
                  {secondary}
                </Text>
              )}
            </>
          }
        />
        <Text variant="label" size={supportingSize} color="subtle">
          {supporting}
        </Text>
      </List.Content>
    </List.Item>
  );
};
